import { CHART_CONFIG, CHART_MESSAGES } from '@constants/constants';

export interface ChartDataItem {
  vendorName: string;
  count: number;
  color: string;
}

export interface SegmentAngle {
  start: number;
  end: number;
  index: number;
}

export class ChartUtilities {
  
  static getChartColors(): string[] {
    try {
      const style = getComputedStyle(document.documentElement);
      const chartColorsVar = style.getPropertyValue('--chart-colors').trim();
      
      if (chartColorsVar) {
        const colors = chartColorsVar.split(',').map(color => color.trim());
        if (colors.length > 0) {
          return colors;
        }
      }
    } catch (error) {
      // Silent fallback
    }
    return [
      'var(--chart-plum)', 'var(--chart-slate-gray)', 'var(--chart-terra-cotta)', 'var(--chart-rust-brown)',
      'var(--chart-sage-green)', 'var(--chart-royal-purple)', 'var(--chart-dark-blue)', 'var(--chart-steel-blue)',
      'var(--chart-purple-alt)', 'var(--chart-gray-alt)', 'var(--chart-brown)', 'var(--chart-sage-light)',
      'var(--chart-mauve)', 'var(--chart-primary-brown)', 'var(--chart-primary-cadet-blue)', 'var(--chart-primary-royal-blue)',
      'var(--chart-primary-blue-violet)', 'var(--chart-primary-tomato)', 'var(--chart-primary-lime-green)', 'var(--chart-primary-gold)',
      'var(--chart-primary-hot-pink)', 'var(--chart-secondary-red)', 'var(--chart-secondary-blue)', 'var(--chart-secondary-yellow)'
    ];
  }

  static initializeChartData(vendorDetails: any[]): ChartDataItem[] {
    const colors = ChartUtilities.getChartColors();
    
    return vendorDetails.map((vendor, index) => ({
      vendorName: vendor.employer || vendor.vendorName || CHART_MESSAGES.UNKNOWN_VENDOR,
      count: vendor.totalEmployees || vendor.count || vendor.candidateCount || 0,
      color: colors[index % colors.length]
    }));
  }

  static calculateCanvasSize(container: HTMLElement | null): number {
    const containerWidth = container?.clientWidth || CHART_CONFIG.CANVAS.DEFAULT_CONTAINER_SIZE;
    const containerHeight = container?.clientHeight || CHART_CONFIG.CANVAS.DEFAULT_CONTAINER_SIZE;
    const size = Math.min(containerWidth, containerHeight, CHART_CONFIG.CANVAS.MAX_SIZE);
    return Math.max(size, CHART_CONFIG.CANVAS.MIN_SIZE);
  }

  static setupHighDPICanvas(canvas: HTMLCanvasElement, displaySize: number): CanvasRenderingContext2D | null {
    const context = canvas.getContext('2d');
    if (!context) return null;

    const dpr = window.devicePixelRatio || 1;
    const actualSize = displaySize * dpr;
    
    canvas.width = actualSize;
    canvas.height = actualSize;
    
    canvas.style.width = `${displaySize}px`;
    canvas.style.height = `${displaySize}px`;
    
    context.scale(dpr, dpr);
    
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    return context;
  }

  static drawWrappedText(
    ctx: CanvasRenderingContext2D, 
    text: string, 
    x: number, 
    y: number, 
    maxWidth: number, 
    lineHeight: number, 
    textAlign: CanvasTextAlign
  ): void {
    const words = text.split(' ');
    let line = '';
    const lines: string[] = [];
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      
      if (testWidth > maxWidth && i > 0) {
        lines.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    
    const totalHeight = lines.length * lineHeight;
    const startY = y - (totalHeight / 2) + (lineHeight / 2);
    
    lines.forEach((line, index) => {
      ctx.textAlign = textAlign;
      ctx.textBaseline = 'middle';
      ctx.fillText(line, x, startY + (index * lineHeight));
    });
  }

  static getTextAlignment(labelX: number, centerX: number): CanvasTextAlign {
    if (labelX < centerX - CHART_CONFIG.CANVAS.TEXT_ALIGNMENT_THRESHOLD) {
      return 'right';
    } else if (labelX > centerX + CHART_CONFIG.CANVAS.TEXT_ALIGNMENT_THRESHOLD) {
      return 'left';
    }
    return 'center';
  }

  static normalizeAngle(angle: number): number {
    return angle < 0 ? angle + 2 * Math.PI : angle;
  }
}
export function buildDoughnutChartConfig(vendorDetails: any[], chartColors: string[]) {
  const chartData = {
    labels: vendorDetails.map((vendor) => vendor.employer),
    datasets: [
      {
        data: vendorDetails.map((vendor) => vendor.totalEmployees),
        backgroundColor: chartColors,
        hoverBackgroundColor: chartColors,
        borderColor: CHART_CONFIG.COLORS.WHITE,
        borderWidth: CHART_CONFIG.STROKE.CHART_WIDTH,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      font: {
        size: 12,
        family: 'Inter',
        weight: '400',
        lineHeight: 1.5,
      },
      /* counts live in the side legend now — labels floating around the ring
         cluttered the card and the 60px layout padding shrank the donut */
      datalabels: { display: false },
    },
    layout: { padding: 4 },
    cutout: '62%',
  };

  return { chartData, chartOptions };
}